/**
 * Engine-neutral path picking + route overlay for the Path Profile section.
 *
 * The 2D map is MapLibre on GPU hosts and the OpenLayers canvas engine on
 * software-rendering hosts, so gating on MapLibre alone made the tool look
 * unavailable in exactly the sessions that fall back. Both engines expose what
 * the tool needs, behind one small surface.
 */

import { getMapLibreHostHandle } from '@/engines/maplibre/hostHandle';
import { getCanvasMapHostHandle } from '@/engines/openlayers/hostHandle';
import { tokens } from '@/design';
import type { PathProfileCoordinate } from './pathProfile';

const SOURCE_ID = 'geosoft-path-profile';
const LINE_LAYER_ID = `${SOURCE_ID}-line`;
const VERTEX_LAYER_ID = `${SOURCE_ID}-points`;
const CURSOR_LAYER_ID = `${SOURCE_ID}-cursor`;

export interface PathPickHandlers {
  /** A vertex was added to the route. */
  readonly onPoint: (lonLat: PathProfileCoordinate) => void;
  /** The operator double-clicked to close the route. */
  readonly onFinish: (lonLat: PathProfileCoordinate) => void;
}

export interface PathDrawSurface {
  readonly engine: 'maplibre' | 'openlayers';
  /** Start picking; returns a detach function, or null when the map went away. */
  attach: (handlers: PathPickHandlers) => (() => void) | null;
  render: (
    vertices: readonly PathProfileCoordinate[],
    cursor: PathProfileCoordinate | null,
  ) => void;
  clear: () => void;
}

function profileGeoJson(
  vertices: readonly PathProfileCoordinate[],
  cursor: PathProfileCoordinate | null,
): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  if (vertices.length >= 2) {
    features.push({
      type: 'Feature',
      properties: { role: 'path' },
      geometry: { type: 'LineString', coordinates: vertices.map((v) => [...v]) },
    });
  }
  for (const vertex of vertices) {
    features.push({
      type: 'Feature',
      properties: { role: 'vertex' },
      geometry: { type: 'Point', coordinates: [...vertex] },
    });
  }
  if (cursor) {
    features.push({
      type: 'Feature',
      properties: { role: 'cursor' },
      geometry: { type: 'Point', coordinates: [...cursor] },
    });
  }
  return { type: 'FeatureCollection', features };
}

function createMapLibreSurface(container: HTMLElement): PathDrawSurface | null {
  if (!getMapLibreHostHandle(container)) return null;

  return {
    engine: 'maplibre',
    attach(handlers) {
      const map = getMapLibreHostHandle(container);
      if (!map) return null;

      const canvas = map.getCanvas();
      const previousCursor = canvas.style.cursor;
      const zoomWasEnabled = map.doubleClickZoom.isEnabled();
      map.doubleClickZoom.disable();
      canvas.style.cursor = 'crosshair';

      const onClick = (event: { lngLat: { lng: number; lat: number } }) => {
        handlers.onPoint([event.lngLat.lng, event.lngLat.lat]);
      };
      const onDoubleClick = (event: {
        lngLat: { lng: number; lat: number };
        preventDefault: () => void;
      }) => {
        event.preventDefault();
        handlers.onFinish([event.lngLat.lng, event.lngLat.lat]);
      };
      map.on('click', onClick);
      map.on('dblclick', onDoubleClick);

      return () => {
        try {
          map.off('click', onClick);
          map.off('dblclick', onDoubleClick);
          if (zoomWasEnabled) map.doubleClickZoom.enable();
          canvas.style.cursor = previousCursor;
        } catch {
          // The map may already be torn down.
        }
      };
    },
    render(vertices, cursor) {
      const map = getMapLibreHostHandle(container);
      if (!map?.isStyleLoaded()) return;
      const data = profileGeoJson(vertices, cursor);
      const source = map.getSource(SOURCE_ID) as
        | { setData: (next: GeoJSON.FeatureCollection) => void }
        | undefined;
      if (source) {
        source.setData(data);
        return;
      }
      map.addSource(SOURCE_ID, { type: 'geojson', data });
      map.addLayer({
        id: LINE_LAYER_ID,
        type: 'line',
        source: SOURCE_ID,
        filter: ['==', ['get', 'role'], 'path'],
        paint: {
          'line-color': tokens.color.semantic.warning,
          'line-width': 3,
        },
      });
      map.addLayer({
        id: VERTEX_LAYER_ID,
        type: 'circle',
        source: SOURCE_ID,
        filter: ['==', ['get', 'role'], 'vertex'],
        paint: {
          'circle-radius': 5,
          'circle-color': tokens.color.semantic.warning,
          'circle-stroke-width': 2,
          'circle-stroke-color': tokens.color.ink.inverse,
        },
      });
      map.addLayer({
        id: CURSOR_LAYER_ID,
        type: 'circle',
        source: SOURCE_ID,
        filter: ['==', ['get', 'role'], 'cursor'],
        paint: {
          'circle-radius': 7,
          'circle-color': tokens.color.accent.primary,
          'circle-stroke-width': 2,
          'circle-stroke-color': tokens.color.ink.inverse,
        },
      });
    },
    clear() {
      const map = getMapLibreHostHandle(container);
      if (!map) return;
      try {
        for (const id of [CURSOR_LAYER_ID, VERTEX_LAYER_ID, LINE_LAYER_ID]) {
          if (map.getLayer(id)) map.removeLayer(id);
        }
        if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
      } catch {
        // The map/style may already be tearing down.
      }
    },
  };
}

function createCanvasSurface(container: HTMLElement): PathDrawSurface | null {
  const adapter = getCanvasMapHostHandle(container);
  if (!adapter || typeof adapter.subscribePathProfilePicks !== 'function') return null;

  return {
    engine: 'openlayers',
    attach(handlers) {
      return (
        getCanvasMapHostHandle(container)?.subscribePathProfilePicks(handlers) ?? null
      );
    },
    render(vertices, cursor) {
      getCanvasMapHostHandle(container)?.renderPathProfileOverlay(vertices, cursor);
    },
    clear() {
      getCanvasMapHostHandle(container)?.clearPathProfileOverlay();
    },
  };
}

/**
 * Resolve whichever 2D engine is mounted in `container`. Returns null while a
 * 3D-only engine (Giro3D) owns the view, or before the map host attaches.
 */
export function resolvePathDrawSurface(
  container: HTMLElement | null | undefined,
): PathDrawSurface | null {
  if (!container) return null;
  return createMapLibreSurface(container) ?? createCanvasSurface(container);
}
